import { useEffect, useMemo, useState } from "react"

import { useMutation, useQuery } from "@tanstack/react-query"
import {
  type ColumnFiltersState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { produce } from "immer"
import { Edit, X } from "lucide-react"
import { NavLink } from "react-router"
import { toast } from "sonner"

import { queryClient } from "@/App.tsx"
import LocationFilter from "@/components/LocationFilter.tsx"
import { Button } from "@/components/ui/button.tsx"
import { Input } from "@/components/ui/input.tsx"
import { ScrollArea } from "@/components/ui/scroll-area.tsx"
import { Skeleton } from "@/components/ui/skeleton.tsx"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.tsx"
import type { DataPoint } from "@/types/dataPointTypes.ts"

import { deleteDataPoint } from "../api/deleteDataPoint.ts"
import { getAllDataPoints } from "../api/getAllDataPoints.ts"

const columnHelper = createColumnHelper<DataPoint>()

export default function DataPointTable({
  variant,
  onSelect = () => {},
}: {
  variant: "page" | "selector"
  onSelect?: (id: number) => void
}) {
  const { data, isPending, isError } = useQuery({
    queryKey: ["allDataPoints"],
    queryFn: () => getAllDataPoints(),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteDataPoint(id),
    onSuccess: () => {
      toast("Data Point deleted")
      queryClient.invalidateQueries({ queryKey: ["allDataPoints"] })
    },
  })

  const columnDefs = useMemo(
    () => [
      columnHelper.accessor((dp) => dp.name, {
        id: "name",
        header: ({ column }) => (
          <div className="p-4 w-[400px] sticky top-0">
            <Input
              className="bg-white"
              placeholder="Search data points"
              value={column.getFilterValue() as string}
              onChange={(e) => column.setFilterValue(e.target.value)}
              key="search-dp"
              autoFocus
            />
          </div>
        ),
        filterFn: "includesString",
        cell: (context) => (
          <div className="p-4">
            {variant == "page" ? (
              context.getValue()
            ) : (
              <Button variant="ghost" onClick={() => onSelect(context.row.original.id)}>
                {context.getValue()}
              </Button>
            )}
          </div>
        ),
      }),
      columnHelper.accessor((dp) => dp.locationId, {
        id: "location",
        filterFn: (row, _, filterValue: number[]) => filterValue.includes(row.original.locationId),
        header: ({ column }) => {
          const filter = column.getFilterValue() as number[]
          return (
            <div className="p-4 text-center sticky top-0">
              <LocationFilter
                selected={filter}
                setSelected={(id, selected) => {
                  column.setFilterValue(
                    produce(filter, (draft) => {
                      const index = draft.findIndex((l) => l == id)
                      if (selected && index == -1) {
                        draft.push(id)
                      } else if (!selected && index != -1) {
                        draft.splice(index, 1)
                      }
                    }),
                  )
                }}
              />
            </div>
          )
        },
        cell: ({ row }) => <div className="p-4 text-center">{row.original.location || ""}</div>,
      }),
      columnHelper.accessor((dp) => dp.$type, {
        id: "type",
        header: () => <div className="p-4 text-center sticky top-0">Type</div>,
        cell: (context) => <div className="p-4 text-center">{context.getValue()}</div>,
      }),
      columnHelper.display({
        id: "edit",

        header: () => <div className="text-center w-[100px] sticky top-0">Edit</div>,
        cell: ({ row }) => (
          <div className="text-center w-[100px]">
            <NavLink to={`/datapoints/defs/${row.original.id}`}>
              <Button variant="ghost" disabled={deleteMutation.isPending}>
                <Edit />
              </Button>
            </NavLink>
          </div>
        ),
      }),
      columnHelper.display({
        id: "delete",
        header: () => <div className="text-center w-[100px] sticky top-0">Delete</div>,
        cell: ({ row }) => (
          <div className="text-center w-[100px]">
            <Button
              variant="ghost"
              onClick={() => deleteMutation.mutate(row.original.id)}
              disabled={deleteMutation.isPending}
            >
              <X />
            </Button>
          </div>
        ),
      }),
    ],
    [deleteMutation, onSelect, variant],
  )

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([
    {
      id: "location",
      value: [],
    },
    { id: "name", value: "" },
  ])

  useEffect(() => {
    if (data != undefined) {
      setColumnFilters(
        produce((draft) => {
          const locations = [...new Set(data ? data.map((dp) => dp.locationId) : [])]
          const locationFilter = draft.find((f) => f.id == "location")
          if (locationFilter) locationFilter.value = locations
        }),
      )
    }
  }, [data])

  const table = useReactTable<DataPoint>({
    data: data || [],
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    state: {
      columnFilters,
      columnVisibility: {
        edit: variant == "page" ? true : false,
        delete: variant == "page" ? true : false,
      },
    },
  })

  if (isPending)
    return (
      <div className="h-[300px] w-full p-4">
        <Skeleton className="h-full w-full" />
      </div>
    )
  if (isError) return <div className="h-[300px] w-full p-4">Network Error</div>

  return (
    <ScrollArea className="h-full">
      <Table className="m-h-full overflow-y-hidden">
        {table.getHeaderGroups().map((hg) => (
          <TableHeader className="bg-accent border-b sticky top-0" key={hg.id}>
            <TableRow>
              {hg.headers.map((header, i) => (
                <TableHead key={i}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
        ))}
        <TableBody>
          {table.getRowModel()?.rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell, i) => (
                <TableCell key={i}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </ScrollArea>
  )
}
